/**
 * MCG LED Uploader Specifications
 * 
 * Complex uploader with multiple LED formats:
 * - Tiled: 4 levels, 1 file each, must end with _MCG-TL-{level}
 * - Overlay: 5 levels (FOP, L2W, L2B, L3, L4), 1 file each, must end with _MCG-OL-{level}
 * - 360: 4 levels, multiple segments each, must end with _MCG-{level}-{segment}
 * - Interchange: Single asset for all levels
 * - Ponsford: Single asset, requires prior booking
 * - Robbie Flowers: Single asset, requires prior booking
 * 
 * Key features:
 * - Filename validation (must end with segment ID)
 * - Duration consistency (all clips must match)
 * - Per-segment dimension validation
 * - Grouped asset tracking with status
 */

// ============================================================
// LED FORMAT OPTIONS (for format selector)
// ============================================================
export const LED_FORMATS = [
  { id: "tiled", label: "Tiled" },
  { id: "360", label: "360" },
  { id: "interchange", label: "Interchange" },
  { id: "overlay", label: "Overlay" },
  { id: "ponsford", label: "Ponsford Stage" },
  { id: "robbie-flowers", label: "Robbie Flowers" },
  { id: "desk", label: "Interview Desk" },
];

// ============================================================
// COMMON VIDEO SPEC (shared across LED types)
// ============================================================
const LED_VIDEO_COMMON = {
  allowed: true,
  frameRate: 50,
  extensions: ["mp4"],
  formats: ["AVC"],
  audio: { mode: "optional" },
  requiresAlpha: false,
  inspectAlpha: false,
};

const LED_IMAGE_COMMON = {
  allowed: true,
  extensions: ["png", "jpg", "jpeg"],
  requiresAlpha: false,
  inspectAlpha: false,
};

// Overlay accepts MOV / ProRes 4444 (alpha channel content)
const LED_OVERLAY_VIDEO_COMMON = {
  ...LED_VIDEO_COMMON,
  extensions: ["mp4", "mov"],
  formats: ["AVC", "ProRes"],
  requiresAlpha: false,
  inspectAlpha: true,
  requireProRes4444: true, // ProRes must be 4444 profile — 422/HQ variants rejected
};

// Duration ranges
const LED_DURATION_RANGE = { min: 5, max: 180 }; // 5s - 3:00
const INTERCHANGE_DURATION = 10; // Exact 10s
const PONSFORD_DURATION = 30; // Exact 30s
const ROBBIE_FLOWERS_DURATION = 30; // Exact 30s

// ============================================================
// TILED LED SPECS (4 levels, 1 file each)
// ============================================================
export const TILED_SPECS = {
  "MCG-TL-FOP": {
    label: "Tiled LED – Field of Play",
    uploadId: "MCG-TL-FOP",
    ledType: "tiled",
    level: "FOP",
    contentGroup: "groupedAssets",
    maxFiles: 1,
    requiredFiles: 1,
    filenamePattern: /(?:^|[_])MCG-TL-FOP\.[^.]+$/i,
    filenameHint: "Filename must end with _MCG-TL-FOP (before the extension)",
    durationRange: LED_DURATION_RANGE,
    
    dimensions: [{ width: 2370, height: 96 }],
    
    videoSpec: {
      ...LED_VIDEO_COMMON,
      width: 2370,
      height: 96,
    },
    
    imageSpec: {
      ...LED_IMAGE_COMMON,
      width: 2370,
      height: 96,
    },
  },
  
  "MCG-TL-L2": {
    label: "Tiled LED – Level 2",
    uploadId: "MCG-TL-L2",
    ledType: "tiled",
    level: "L2",
    contentGroup: "groupedAssets",
    maxFiles: 1,
    requiredFiles: 1,
    filenamePattern: /(?:^|[_])MCG-TL-L2\.[^.]+$/i,
    filenameHint: "Filename must end with _MCG-TL-L2 (before the extension)",
    durationRange: LED_DURATION_RANGE,
    
    dimensions: [{ width: 2400, height: 90 }],
    
    videoSpec: {
      ...LED_VIDEO_COMMON,
      width: 2400,
      height: 90,
    },
    
    imageSpec: {
      ...LED_IMAGE_COMMON,
      width: 2400,
      height: 90,
    },
  },
  
  "MCG-TL-L3": {
    label: "Tiled LED – Level 3",
    uploadId: "MCG-TL-L3",
    ledType: "tiled",
    level: "L3",
    contentGroup: "groupedAssets",
    maxFiles: 1,
    requiredFiles: 1,
    filenamePattern: /(?:^|[_])MCG-TL-L3\.[^.]+$/i,
    filenameHint: "Filename must end with _MCG-TL-L3 (before the extension)",
    durationRange: LED_DURATION_RANGE,
    
    dimensions: [{ width: 1850, height: 90 }],
    
    videoSpec: {
      ...LED_VIDEO_COMMON,
      width: 1850,
      height: 90,
    },
    
    imageSpec: {
      ...LED_IMAGE_COMMON,
      width: 1850,
      height: 90,
    },
  },
  
  "MCG-TL-L4": {
    label: "Tiled LED – Level 4",
    uploadId: "MCG-TL-L4",
    ledType: "tiled",
    level: "L4",
    contentGroup: "groupedAssets",
    maxFiles: 1,
    requiredFiles: 1,
    filenamePattern: /(?:^|[_])MCG-TL-L4\.[^.]+$/i,
    filenameHint: "Filename must end with _MCG-TL-L4 (before the extension)",
    durationRange: LED_DURATION_RANGE,
    
    dimensions: [{ width: 2000, height: 90 }],
    
    videoSpec: {
      ...LED_VIDEO_COMMON,
      width: 2000,
      height: 90,
    },
    
    imageSpec: {
      ...LED_IMAGE_COMMON,
      width: 2000,
      height: 90,
    },
  },
};

// ============================================================
// OVERLAY LED SPECS (5 levels, 1 file each)
// ============================================================
export const OVERLAY_SPECS = {
  "MCG-OL-FOP": {
    label: "MCG Overlay LED – Field of Play",
    uploadId: "MCG-OL-FOP",
    ledType: "overlay",
    level: "FOP",
    contentGroup: "groupedAssets",
    maxFiles: 1,
    requiredFiles: 1,
    filenamePattern: /(?:^|[_])MCG-OL-FOP\.[^.]+$/i,
    filenameHint: "Filename must end with _MCG-OL-FOP (before the extension)",
    durationRange: LED_DURATION_RANGE,
    
    dimensions: [{ width: 1920, height: 90 }],
    
    videoSpec: {
      ...LED_OVERLAY_VIDEO_COMMON,
      width: 1920,
      height: 90,
    },
    
    imageSpec: {
      ...LED_IMAGE_COMMON,
      width: 1920,
      height: 90,
    },
  },
  
  "MCG-OL-L2W": {
    label: "MCG Overlay LED – Level 2 Wing",
    uploadId: "MCG-OL-L2W",
    ledType: "overlay",
    level: "L2W",
    contentGroup: "groupedAssets",
    maxFiles: 1,
    requiredFiles: 1,
    filenamePattern: /(?:^|[_])MCG-OL-L2W\.[^.]+$/i,
    filenameHint: "Filename must end with _MCG-OL-L2W (before the extension)",
    durationRange: LED_DURATION_RANGE,
    
    dimensions: [{ width: 1920, height: 90 }],
    
    videoSpec: {
      ...LED_OVERLAY_VIDEO_COMMON,
      width: 1920,
      height: 90,
    },
    
    imageSpec: {
      ...LED_IMAGE_COMMON,
      width: 1920,
      height: 90,
    },
  },
  
  "MCG-OL-L2B": {
    label: "MCG Overlay LED – Level 2 Camera Basket",
    uploadId: "MCG-OL-L2B",
    ledType: "overlay",
    level: "L2B",
    contentGroup: "groupedAssets",
    maxFiles: 1,
    requiredFiles: 1,
    filenamePattern: /(?:^|[_])MCG-OL-L2B\.[^.]+$/i,
    filenameHint: "Filename must end with _MCG-OL-L2B (before the extension)",
    durationRange: LED_DURATION_RANGE,
    
    dimensions: [{ width: 1920, height: 90 }],
    
    videoSpec: {
      ...LED_OVERLAY_VIDEO_COMMON,
      width: 1920,
      height: 90,
    },
    
    imageSpec: {
      ...LED_IMAGE_COMMON,
      width: 1920,
      height: 90,
    },
  },
  
  "MCG-OL-L3": {
    label: "MCG Overlay LED – Level 3",
    uploadId: "MCG-OL-L3",
    ledType: "overlay",
    level: "L3",
    contentGroup: "groupedAssets",
    maxFiles: 1,
    requiredFiles: 1,
    filenamePattern: /(?:^|[_])MCG-OL-L3\.[^.]+$/i,
    filenameHint: "Filename must end with _MCG-OL-L3 (before the extension)",
    durationRange: LED_DURATION_RANGE,
    
    dimensions: [{ width: 1920, height: 90 }],
    
    videoSpec: {
      ...LED_OVERLAY_VIDEO_COMMON,
      width: 1920,
      height: 90,
    },
    
    imageSpec: {
      ...LED_IMAGE_COMMON,
      width: 1920,
      height: 90,
    },
  },
  
  "MCG-OL-L4": {
    label: "MCG Overlay LED – Level 4",
    uploadId: "MCG-OL-L4",
    ledType: "overlay",
    level: "L4",
    contentGroup: "groupedAssets",
    maxFiles: 1,
    requiredFiles: 1,
    filenamePattern: /(?:^|[_])MCG-OL-L4\.[^.]+$/i,
    filenameHint: "Filename must end with _MCG-OL-L4 (before the extension)",
    durationRange: LED_DURATION_RANGE,
    
    dimensions: [{ width: 1920, height: 90 }],
    
    videoSpec: {
      ...LED_OVERLAY_VIDEO_COMMON,
      width: 1920,
      height: 90,
    },
    
    imageSpec: {
      ...LED_IMAGE_COMMON,
      width: 1920,
      height: 90,
    },
  },
};

// ============================================================
// 360 LED SPECS (4 levels, multiple segments each)
// ============================================================
export const THREE_SIXTY_SPECS = {
  "MCG-FOP": {
    label: "360 LED – Field of Play",
    uploadId: "MCG-FOP",
    ledType: "360",
    level: "FOP",
    contentGroup: "groupedAssets",
    maxFiles: 5,
    requiredFiles: 5,
    durationRange: LED_DURATION_RANGE,
    filenameHint: "Filename must end with one of: _MCG-FOP-1, _MCG-FOP-2, _MCG-FOP-3, _MCG-FOP-4, _MCG-FOP-5",
    
    // Common video spec (framerate, codec, etc)
    commonVideoSpec: LED_VIDEO_COMMON,
    imageSpec: LED_IMAGE_COMMON,
    
    // Per-segment dimensions
    segments: {
      "MCG-FOP-1": { width: 8208, height: 96 },
      "MCG-FOP-2": { width: 8208, height: 96 },
      "MCG-FOP-3": { width: 13760, height: 96 },
      "MCG-FOP-4": { width: 8624, height: 96 },
      "MCG-FOP-5": { width: 8624, height: 96 },
    },
  },
  
  "MCG-L2": {
    label: "360 LED – Level 2",
    uploadId: "MCG-L2",
    ledType: "360",
    level: "L2",
    contentGroup: "groupedAssets",
    maxFiles: 8,
    requiredFiles: 8,
    durationRange: LED_DURATION_RANGE,
    filenameHint: "Filename must end with one of: _MCG-L2-1 through _MCG-L2-8",
    
    commonVideoSpec: LED_VIDEO_COMMON,
    imageSpec: LED_IMAGE_COMMON,
    
    segments: {
      "MCG-L2-1": { width: 9360, height: 90 },
      "MCG-L2-2": { width: 3960, height: 90 },
      "MCG-L2-3": { width: 2640, height: 90 },
      "MCG-L2-4": { width: 12900, height: 90 },
      "MCG-L2-5": { width: 12900, height: 90 },
      "MCG-L2-6": { width: 4920, height: 90 },
      "MCG-L2-7": { width: 6240, height: 90 },
      "MCG-L2-8": { width: 2400, height: 90 },
    },
  },
  
  "MCG-L3": {
    label: "360 LED – Level 3",
    uploadId: "MCG-L3",
    ledType: "360",
    level: "L3",
    contentGroup: "groupedAssets",
    maxFiles: 2,
    requiredFiles: 2,
    durationRange: LED_DURATION_RANGE,
    filenameHint: "Filename must end with one of: _MCG-L3-1, _MCG-L3-2",
    
    commonVideoSpec: LED_VIDEO_COMMON,
    imageSpec: LED_IMAGE_COMMON,
    
    segments: {
      "MCG-L3-1": { width: 14460, height: 90 },
      "MCG-L3-2": { width: 14460, height: 90 },
    },
  },
  
  "MCG-L4": {
    label: "360 LED – Level 4",
    uploadId: "MCG-L4",
    ledType: "360",
    level: "L4",
    contentGroup: "groupedAssets",
    maxFiles: 6,
    requiredFiles: 6,
    durationRange: LED_DURATION_RANGE,
    filenameHint: "Filename must end with one of: _MCG-L4-1 through _MCG-L4-6",
    
    commonVideoSpec: LED_VIDEO_COMMON,
    imageSpec: LED_IMAGE_COMMON,
    
    segments: {
      "MCG-L4-1": { width: 7440, height: 90 },
      "MCG-L4-2": { width: 5520, height: 90 },
      "MCG-L4-3": { width: 13920, height: 90 },
      "MCG-L4-4": { width: 14400, height: 90 },
      "MCG-L4-5": { width: 6600, height: 90 },
      "MCG-L4-6": { width: 7320, height: 90 },
    },
  },
};

// ============================================================
// INTERCHANGE LED SPEC (single asset for all levels)
// ============================================================
export const INTERCHANGE_SPEC = {
  "MCG-INT": {
    label: "Interchange LED",
    uploadId: "MCG-INT",
    ledType: "interchange",
    contentGroup: "singleAsset",
    maxFiles: 1,
    requiredFiles: 1,
    filenamePattern: /(?:^|[_])MCG-INT\.[^.]+$/i,
    filenameHint: "Filename must end with _MCG-INT",
    exactDuration: INTERCHANGE_DURATION,
    
    dimensions: [{ width: 3744, height: 90 }],
    
    videoSpec: {
      ...LED_VIDEO_COMMON,
      width: 3744,
      height: 90,
      duration: INTERCHANGE_DURATION,
    },
    
    imageSpec: {
      ...LED_IMAGE_COMMON,
      width: 3744,
      height: 90,
    },
  },
};

// ============================================================
// PONSFORD STAGE SPEC (requires prior booking)
// ============================================================
export const PONSFORD_SPEC = {
  "MCG-PONS": {
    label: "Ponsford Stage",
    uploadId: "MCG-PONS",
    ledType: "ponsford",
    contentGroup: "singleAsset",
    maxFiles: 1,
    requiredFiles: 1,
    filenamePattern: /(?:^|[_])MCG-PONS\.[^.]+$/i,
    filenameHint: "Filename must end with _MCG-PONS",
    exactDuration: PONSFORD_DURATION,
    requiresBooking: true,
    
    dimensions: [{ width: 576, height: 80 }],
    
    videoSpec: {
      ...LED_VIDEO_COMMON,
      width: 576,
      height: 80,
      duration: PONSFORD_DURATION,
    },
    
    imageSpec: {
      ...LED_IMAGE_COMMON,
      width: 576,
      height: 80,
    },
  },
};

// ============================================================
// ROBBIE FLOWERS SPEC (requires prior booking)
// ============================================================
export const ROBBIE_FLOWERS_SPEC = {
  "MCG-RFLO": {
    label: "Robbie Flowers",
    uploadId: "MCG-RFLO",
    ledType: "robbie-flowers",
    contentGroup: "singleAsset",
    maxFiles: 1,
    requiredFiles: 1,
    filenamePattern: /(?:^|[_])MCG-RFLO\.[^.]+$/i,
    filenameHint: "Filename must end with _MCG-RFLO",
    exactDuration: ROBBIE_FLOWERS_DURATION,
    
    dimensions: [{ width: 1850, height: 90 }],
    
    videoSpec: {
      ...LED_VIDEO_COMMON,
      width: 1850,
      height: 90,
      duration: ROBBIE_FLOWERS_DURATION,
    },
    
    imageSpec: {
      ...LED_IMAGE_COMMON,
      width: 1850,
      height: 90,
    },
  },
};

// ============================================================
// INTERVIEW DESK SPEC (single asset)
// ============================================================
export const DESK_SPEC = {
  "MCG-DESK": {
    label: "Interview Desk",
    uploadId: "MCG-DESK",
    ledType: "desk",
    contentGroup: "singleAsset",
    maxFiles: 1,
    requiredFiles: 1,
    filenamePattern: /(?:^|[_])MCG-DESK\.[^.]+$/i,
    filenameHint: "Filename must end with _MCG-DESK",
    durationRange: LED_DURATION_RANGE,
    
    dimensions: [{ width: 1920, height: 960 }],
    
    videoSpec: {
      ...LED_VIDEO_COMMON,
      frameRate: 25,
      width: 1920,
      height: 960,
    },
    
    imageSpec: {
      ...LED_IMAGE_COMMON,
      width: 1920,
      height: 960,
    },
  },
};

// ============================================================
// ALL SPECS BY FORMAT
// ============================================================
export const SPECS_BY_FORMAT = {
  tiled: TILED_SPECS,
  overlay: OVERLAY_SPECS,
  "360": THREE_SIXTY_SPECS,
  interchange: INTERCHANGE_SPEC,
  ponsford: PONSFORD_SPEC,
  "robbie-flowers": ROBBIE_FLOWERS_SPEC,
  desk: DESK_SPEC,
};

// ============================================================
// ALL SPECS (flat object for lookup)
// ============================================================
export const ALL_SPECS = {
  ...TILED_SPECS,
  ...OVERLAY_SPECS,
  ...THREE_SIXTY_SPECS,
  ...INTERCHANGE_SPEC,
  ...PONSFORD_SPEC,
  ...ROBBIE_FLOWERS_SPEC,
  ...DESK_SPEC,
};

// ============================================================
// HELPER: Get segment ID from filename
// ============================================================
export function getSegmentIdFromFilename(filename, segments) {
  if (!segments) return null;
  
  const upper = filename.toUpperCase();
  
  return Object.keys(segments).find((segmentId) => {
    const re = new RegExp(`(?:^|[_])${segmentId}\\.[^.]+$`, "i");
    return re.test(upper);
  }) || null;
}

// ============================================================
// HELPER: Check if filename matches pattern
// ============================================================
export function validateFilename(filename, spec) {
  // For 360 specs with segments
  if (spec.segments) {
    const segmentId = getSegmentIdFromFilename(filename, spec.segments);
    return {
      valid: !!segmentId,
      segmentId,
      hint: spec.filenameHint,
    };
  }
  
  // For specs with explicit pattern
  if (spec.filenamePattern) {
    return {
      valid: spec.filenamePattern.test(filename),
      segmentId: null,
      hint: spec.filenameHint,
    };
  }
  
  // Fallback: check if ends with uploadId
  const re = new RegExp(`(?:^|[_])${spec.uploadId}\\.[^.]+$`, "i");
  return {
    valid: re.test(filename),
    segmentId: null,
    hint: `Filename must end with _${spec.uploadId}`,
  };
}

// ============================================================
// HELPER: Get dimensions for a file
// ============================================================
export function getDimensionsForFile(filename, spec) {
  // For 360 specs with segments
  if (spec.segments) {
    const segmentId = getSegmentIdFromFilename(filename, spec.segments);
    if (segmentId) {
      return spec.segments[segmentId];
    }
    return null;
  }
  
  // For specs with direct dimensions
  if (spec.dimensions?.length) {
    return spec.dimensions[0];
  }
  
  // Fallback to videoSpec dimensions
  if (spec.videoSpec?.width && spec.videoSpec?.height) {
    return { width: spec.videoSpec.width, height: spec.videoSpec.height };
  }
  
  return null;
}

// ============================================================
// HELPER: Build spec label for display
// ============================================================
export function buildSpecLabel(spec) {
  const parts = [];

  // Dimensions
  if (spec.segments) {
    const sizes = Object.values(spec.segments)
      .map((s) => `${s.width}×${s.height}`)
      .filter((v, i, a) => a.indexOf(v) === i); // unique
    parts.push(sizes.length > 3 ? "Various dimensions" : sizes.join(" / "));
  } else if (spec.dimensions?.length) {
    parts.push(spec.dimensions.map((d) => `${d.width}×${d.height}`).join(" / "));
  } else if (spec.videoSpec?.width && spec.videoSpec?.height) {
    parts.push(`${spec.videoSpec.width}×${spec.videoSpec.height}`);
  }

  // Video
  const videoSpec = spec.videoSpec || spec.commonVideoSpec;
  if (videoSpec) {
    // Container
    const exts = videoSpec.extensions || [];
    parts.push(exts.includes("mov") ? "MOV / MP4" : "MP4");

    // Codec
    const formats = videoSpec.formats || [];
    if (formats.includes("ProRes") && videoSpec.requireProRes4444) {
      parts.push(formats.includes("AVC") ? "H.264 / ProRes 4444" : "ProRes 4444");
    } else if (formats.includes("AVC")) {
      parts.push("H.264");
    }
    if (videoSpec.frameRate) {
      parts.push(`${videoSpec.frameRate}fps`);
    }
    
    // Duration
    if (spec.exactDuration) {
      parts.push(`Duration: ${spec.exactDuration}s exactly`);
    } else if (spec.durationRange) {
      parts.push(`Duration: ${formatDuration(spec.durationRange.min)} - ${formatDuration(spec.durationRange.max)}`);
    }
    
    parts.push("Looping");
  }

  // Image
  if (spec.imageSpec?.allowed) {
    parts.push("Static JPG / PNG");
    parts.push("RGB");
  }

  return parts.join(" · ");
}

// ============================================================
// HELPER: Format duration for display
// ============================================================
export function formatDuration(seconds) {
  if (typeof seconds !== "number" || isNaN(seconds)) return "";
  
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  
  if (mins > 0) {
    return `${mins}:${String(secs).padStart(2, "0")}`;
  }
  return `${secs}s`;
}

// ============================================================
// METADATA WEBHOOK (LED-specific)
// ============================================================
//export const METADATA_WEBHOOK = "https://hook.eu2.make.com/dd68kvmmcbolqaejfitrrrvl5wiualvq";
export const METADATA_WEBHOOK = "https://hook.eu2.make.com/hs49hn7asl4ow6bx92kx1js28jpehkrt";

const mcgLedSpecs = {
  LED_FORMATS,
  SPECS_BY_FORMAT,
  ALL_SPECS,
  TILED_SPECS,
  OVERLAY_SPECS,
  THREE_SIXTY_SPECS,
  INTERCHANGE_SPEC,
  PONSFORD_SPEC,
  ROBBIE_FLOWERS_SPEC,
  DESK_SPEC,
  getSegmentIdFromFilename,
  validateFilename,
  getDimensionsForFile,
  buildSpecLabel,
  formatDuration,
  METADATA_WEBHOOK,
};

export default mcgLedSpecs;
